<!-- Menu panel template -->
<ng-template #menuPanel>
  <div class="hx-menu-panel">
    <div class="hx-menu-panel__container">
      <div class="hx-menu-panel__content" [class.hx-menu-panel__content--scrollable]="shouldScroll()">
        @for (item of menuItems(); track $index) {
          @if (item.switch) {
            <div class="hx-menu-item-switch" [attr.id]="item.id" [attr.title]="item.tooltip ?? null">
              @if (item.icon) {
                <hx-icon [svgIcon]="item.icon" [size]="16"></hx-icon>
              }
              <span class="hx-menu-item-switch__label">{{ item.label }}</span>
              <hx-switch
                size="2xs"
                [id]="'hx-menu-switch-' + (item.id || $index)"
                [checked]="item.checked ?? false"
                [disabled]="isItemDisabled(item)"
                [ariaLabel]="item.label"
                (checkedChange)="onSwitchToggle(item, $event)"></hx-switch>
            </div>
          } @else if (item.externalUrl) {
            <a
              class="hx-menu-item-link"
              [attr.href]="item.externalUrl"
              [attr.target]="item.openInNewTab === false ? null : '_blank'"
              [attr.rel]="item.openInNewTab === false ? null : 'noopener noreferrer'"
              [attr.aria-label]="item.label"
              [attr.title]="item.tooltip ?? null"
              [attr.tabindex]="isItemDisabled(item) ? -1 : 0"
              [attr.aria-disabled]="isItemDisabled(item)"
              [attr.id]="item.id"
              (click)="onExternalLinkClick($event, item)">
              @if (item.icon) {
                <hx-icon [svgIcon]="item.icon" [size]="16"></hx-icon>
              }
              <span>{{ item.label }}</span>
            </a>
          } @else if (isFontScaleControl(item)) {
            <div class="hx-menu-item-font-scale" role="group" aria-label="Font size controls">
              <button
                hxButton
                variant="ghost"
                context="neutral"
                size="sm"
                type="button"
                [disabled]="isItemDisabled(item)"
                aria-label="Increase font size"
                title="Increase Font Size"
                (click)="onFontScaleIncrease(item)">
                {{ item.label }}
              </button>
              <span class="hx-menu-item-font-scale__badge" role="status" aria-live="polite">
                {{ getFontScaleLabel(item) }}
              </span>
              <button
                hxButton
                variant="ghost"
                context="neutral"
                size="sm"
                type="button"
                [disabled]="item.secondaryDisabled ?? false"
                aria-label="Decrease font size"
                title="Decrease Font Size"
                (click)="onFontScaleDecrease(item)">
                {{ item.secondaryLabel }}
              </button>
              <button
                hxIconButton
                variant="ghost"
                context="neutral"
                size="sm"
                type="button"
                svgIcon="arrow-counterclockwise"
                [disabled]="isFontScaleResetDisabled(item)"
                aria-label="Reset font size"
                title="Reset font size to 100%"
                (click)="onFontScaleReset(item)"></button>
            </div>
          } @else if (item.secondaryLabel) {
            <div class="hx-menu-item-actions" [attr.aria-label]="item.label">
              <button
                hxButton
                variant="ghost"
                [context]="'neutral'"
                [disabled]="isItemDisabled(item)"
                type="button"
                [attr.aria-label]="item.label"
                [attr.title]="item.tooltip ?? null"
                [attr.tabindex]="isItemDisabled(item) ? -1 : 0"
                [attr.aria-disabled]="isItemDisabled(item)"
                [attr.id]="item.id"
                (click)="onMenuItemClick(item, $index)"
                (keydown.enter)="onMenuItemClick(item, $index)"
                (keydown.space)="onMenuItemClick(item, $index)">
                {{ item.label }}
              </button>
              <button
                hxButton
                variant="ghost"
                [context]="'neutral'"
                [disabled]="item.secondaryDisabled ?? false"
                type="button"
                [attr.aria-label]="item.secondaryLabel"
                [attr.title]="item.secondaryTooltip ?? null"
                [attr.tabindex]="item.secondaryDisabled ? -1 : 0"
                [attr.aria-disabled]="item.secondaryDisabled ?? false"
                (click)="onSecondaryMenuItemClick(item, $index)"
                (keydown.enter)="onSecondaryMenuItemClick(item, $index)"
                (keydown.space)="onSecondaryMenuItemClick(item, $index)">
                {{ item.secondaryLabel }}
              </button>
            </div>
          } @else {
            <button
              hxButton
              variant="ghost"
              [context]="'neutral'"
              [startItem]="item.icon ?? null"
              [disabled]="isItemDisabled(item)"
              align="start"
              [attr.tabindex]="isItemDisabled(item) ? -1 : 0"
              [attr.aria-disabled]="isItemDisabled(item)"
              [attr.id]="item.id"
              (click)="onMenuItemClick(item, $index)"
              (keydown.enter)="onMenuItemClick(item, $index)"
              (keydown.space)="onMenuItemClick(item, $index)">
              {{ item.label }}
            </button>
          }

          @if (config().showDividers && $index < menuItems().length - 1) {
            <hx-divider></hx-divider>
          }
        }
      </div>
    </div>
  </div>
</ng-template>

<!-- Custom button template when provided -->
@if (hasCustomTemplate()) {
  <button
    [type]="buttonType()"
    [disabled]="isDisabled()"
    [attr.aria-expanded]="isOpen()"
    [attr.aria-haspopup]="'menu'"
    [attr.aria-label]="buttonLabel()"
    title="Open menu"
    [hxPopover]="menuPanel"
    trigger="click"
    cssStyle="pop-over-overrides"
    position="bottom-start"
    [backdrop]="true"
    [accessible]="true"
    [closeOnScroll]="true"
    (popoverOpened)="onPopoverOpened()"
    (popoverClosed)="onPopoverClosed()">
    <ng-container [ngTemplateOutlet]="buttonTemplate()"></ng-container>
  </button>
}

<!-- Default hx-button when no template is provided -->
@if (!hasCustomTemplate()) {
  <button
    hxButton
    [type]="buttonType()"
    [variant]="variant()"
    [context]="context()"
    [size]="size()"
    [fullWidth]="fullWidth()"
    [startItem]="config().icon ?? null"
    [disabled]="isDisabled()"
    [attr.aria-expanded]="isOpen()"
    [attr.aria-haspopup]="'menu'"
    [attr.aria-label]="buttonLabel()"
    [hxPopover]="menuPanel"
    trigger="click"
    cssStyle="pop-over-overrides"
    position="bottom-start"
    [backdrop]="true"
    [accessible]="true"
    [closeOnScroll]="true"
    (popoverOpened)="onPopoverOpened()"
    (popoverClosed)="onPopoverClosed()">
    @if (!isIconOnly()) {
      {{ buttonLabel() }}
    }
  </button>
}
